import '@/components/header';
import '@/components/img-pin';
import type * as ClassTypes from './types/class';
import {
    initCallbackPopup,
    initCircleSlider,
    initContentPopup,
    initContentSlider,
    initDocs,
    initFullSlider,
    initFullVideo,
    initHorizontalScroll,
    initHorizontalSlider,
    initMapPopup,
    initMobileMenu,
    initParallax,
    initParametric,
    initPopup,
    initScrollSlider,
    initShare,
    initTabs,
    initVideoPlayer,
    lazyLoadElements,
    stepBack
} from './common';
import type {IDynamicImport} from '@/common/scripts/types/utils';
import type {IObserver} from '@/common/scripts/types/observer';
import Observer from '@/common/scripts/observer';
import {RESIZE_EVENTS} from '@/common/scripts/constants';
import {Utils} from '@/common/scripts/utils';

const observer: IObserver = new Observer();

// Табы
const tabs = [...document.querySelectorAll('.j-tabs-wrap')];

tabs.forEach((elem: Element) => {
    initTabs(elem as HTMLElement);
});

// Видео iframe на фоне
const fullIframeVideos = [...document.querySelectorAll('.j-video-iframe')] as HTMLElement[];

fullIframeVideos.forEach((fullIframe: HTMLElement) => {
    initFullVideo(fullIframe);
});

/* Ленивая загрузка */
/* подключать после инициализации слайдеров */
const lazyElements = [...document.querySelectorAll('[loading=lazy]')];

if (lazyElements.length) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            lazyLoadElements(lazyElements);
        });
    } else {
        lazyLoadElements(lazyElements);
    }
}

const docsElems = [...document.querySelectorAll('.j-docs')];

docsElems.forEach((docsElement: Element): void => {
    initDocs(docsElement as HTMLElement);
});

// Контентный слайдер
const verticalSliders = [...document.querySelectorAll('.j-content-slider')];

verticalSliders.forEach((sliderElement: Element): void => {
    initContentSlider(sliderElement as HTMLElement);
});

// Слайдер на весь экран
const fullSlider = [...document.querySelectorAll('.j-slider-full')];

fullSlider.forEach((sliderElement: Element): void => {
    initFullSlider(sliderElement as HTMLElement);
});

// карта в секции с табами
const contentMap = document.querySelector('.j-common-map') as HTMLElement;

if (contentMap) {
    import(/* webpackChunkName: "content-section" */ '@/sections/content-section')
        .then(({default: initContentMap}: IDynamicImport) => {
            initContentMap(contentMap);
        });
}

// Круглый слайдер
const circleSlider = [...document.querySelectorAll('.j-slider-circle')];

circleSlider.forEach((sliderElement: Element): void => {
    initCircleSlider(sliderElement as HTMLElement);
});

// Горизонтальный слайдер с карточками
const horizontalSliders: Element[] = [...document.querySelectorAll('.j-horizontal-slider')];

horizontalSliders.forEach((horizontalSlider: Element): void => {
    initHorizontalSlider(horizontalSlider as HTMLElement);
});

// Поведение меню
const headerWrap = document.querySelector('.j-header') as HTMLElement;
const burgerMenuSelector = document.querySelector('.j-burger') as HTMLElement;
const linksMenuToggle = document.querySelector('.j-links-toggle') as HTMLElement;
const headerWrapper = headerWrap || document.querySelector('header') as HTMLElement;
const menuClose = document.querySelector('.j-navigation-close') as HTMLElement;
const menuLinksClose = document.querySelector('.j-links-close') as HTMLElement;

if (burgerMenuSelector && linksMenuToggle && headerWrapper) {
    initMobileMenu(burgerMenuSelector, linksMenuToggle, menuClose, menuLinksClose, headerWrapper);
}

// Попапы хода строительства
const contentBtns = [...document.querySelectorAll('.j-content-popup')];

contentBtns.forEach((button: Element): void => {
    initContentPopup(button as HTMLElement);
});

// Cкролл по якорям
const anchors = [...document.querySelectorAll('.j-anchor')] as HTMLElement[];

if (anchors.length) {
    import(/* webpackChunkName: "scrollBy" */ '@/components/scrollBy')
        .then(({default: ScrollBy}: IDynamicImport) => {
            const scrollBy: ClassTypes.IScrollBy = new ScrollBy();

            scrollBy.init({
                takeElements: [headerWrap],
                anchors
            });
        });
}

// Параллакс
const parallaxWrappers = [...document.querySelectorAll('.j-parallax-section')];

parallaxWrappers.forEach((wrapper: Element) => {
    initParallax(wrapper as HTMLElement);
});

// Горизонтальный скролл
const horizontalScrollWrappers = [...document.querySelectorAll('.j-horizontal-scroll')];

horizontalScrollWrappers.forEach((wrapper: Element) => {
    initHorizontalScroll(wrapper as HTMLElement);
});

// Видео плеер
const videoPlayers = [...document.querySelectorAll('.j-player')];

videoPlayers.forEach((player: Element) => {
    initVideoPlayer(player as HTMLElement);
});

// scroll slider
const scrollSliderWrapper = [...document.querySelectorAll('.j-scroll-slider-wrapper')];

scrollSliderWrapper.forEach((wrapper: Element) => {
    initScrollSlider(wrapper as HTMLElement);
});

// Попапы
const mapPopupBtns: Element[] = [...document.querySelectorAll('.j-map-popup')];

mapPopupBtns.forEach((button: Element) => {
    initMapPopup(button as HTMLElement);
});

const popupSelectors = [...document.querySelectorAll('.j-popup')] as HTMLElement[];

if (popupSelectors.length) {
    initPopup(popupSelectors, {removeDelay: 300});
}

// Попапы обратной связи
const popupFacadeLinks = [...document.querySelectorAll('.j-popup-callback:not([data-init])')] as HTMLElement[];

if (popupFacadeLinks.length) {
    initCallbackPopup(popupFacadeLinks);

    popupFacadeLinks.forEach((button: HTMLElement) => {
        button.dataset['init'] = 'true';
    });
}

observer.subscribe('popup:refresh', (buttons: any) => {
    initCallbackPopup(buttons);
});

// Шаг назад
const stepBackBtns = [...document.querySelectorAll('.j-back')] as HTMLElement[];

stepBackBtns.forEach((stepBackBtn: HTMLElement): void => {
    stepBack(stepBackBtn);
});

// Уведомление об использовании cookies
const cookiesWrapper = document.querySelector('.j-cookies-notice') as HTMLElement;

if (cookiesWrapper) {
    import(/* webpackChunkName: "cookies-notice" */ '@/components/cookies-notice')
        .then(({default: CookiesNotice}: IDynamicImport) => {
            const cookiesNotice: ClassTypes.ICookiesNotice = new CookiesNotice();

            cookiesNotice.init(cookiesWrapper);
        });
}

document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);

RESIZE_EVENTS.forEach((event: string): void => {
    window.addEventListener(event, Utils.throttle((): void => {
        document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    }, 20));
});

// Шеринг
const shareElements = [...document.querySelectorAll('.j-share')] as HTMLElement[];

if (shareElements.length) {
    initShare(shareElements);
}

// Подключение параметрического
const parametricElement = document.querySelector('.j-parametric') as HTMLElement;

if (parametricElement) {
    initParametric({
        element: parametricElement
    });
}

// Ипотечный калькулятор
const mortgageCalculator = document.querySelector('.j-mortgage-calculator') as HTMLElement;

if (mortgageCalculator) {
    import(/* webpackChunkName: "mortgage-calculator" */ '@/components/mortgage-calculator')
        .then(({default: MortgageCalculator}: IDynamicImport) => {
            const calculator: ClassTypes.IMortgageCalculator = new MortgageCalculator(mortgageCalculator);

            calculator.init();

            // Отображения блока и подстановка месячного платежа ипотеки в карточке квартиры
            const priceMortgageWrappers = [...document.querySelectorAll('.j-price__mortgage')];
            const monthlyPayment = mortgageCalculator.dataset['minMonthly'];

            if (monthlyPayment?.length) {
                priceMortgageWrappers.forEach((wrapper: Element) => {
                    import(/* webpackChunkName: "price-monthly-payment" */ '@/components/price')
                        .then(({default: Price}: IDynamicImport) => {
                            const priceMonthlyPayment: ClassTypes.IPrice = new Price({
                                element: wrapper,
                                payment: monthlyPayment,
                                href   : mortgageCalculator.closest('section')?.id || null
                            });

                            priceMonthlyPayment.init();
                        });
                });
            }
        });
}
